import React, { type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  ApplicationIcon,
  Box,
  ButtonIcon,
  Icon,
  Link,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_BLD, CF_NEU_STL, CF_SEC_MED } from '../../constants';
import { useNavigateTo } from '../../hooks';

import './SodHeader.css';

interface BreadcrumbItem {
  text: string;
  href: string;
}
interface SodHeaderProps {
  title: string | ReactNode;
  description?: string | ReactNode;
  controls?: ReactNode;
  contentBottom?: ReactNode;
  applicationIconKind?: string;
  handleGoBack?: () => void | null;
  className?: string;
  breadcrumbItems?: BreadcrumbItem[];
}

/**
 * * TODO
 * - Write a description
 * - center, and textWrapper, Will need to calc based on the page and header inline content
 * - Fix the SOD Header text truncation, it is broken on hover
 */
function SodHeader({
  title,
  description,
  controls,
  contentBottom,
  applicationIconKind,
  handleGoBack,
  className,
  breadcrumbItems,
}: SodHeaderProps): ReactElement {
  const { navigateToBack } = useNavigateTo();

  const headerClasses = cx('SodHeader-headerWrapper', className);

  const defaultGoBack = () => navigateToBack();

  const getBreadcrumb = () => (
    <Box className='SodHeader-breadcrumb'>
      {breadcrumbItems?.map((item) => (
        <React.Fragment key={`${item.text}`}>
          <Box tag='span' className='SodHeader-breadcrumb-item'>
            <Link
              text={item.text}
              color='brand'
              target='_self'
              href={item.href}
              RouterComponent={RouterLink}
            />
            <Box tag='span' className='SodHeader-breadcrumb-separator'>
              <Icon kind='ChevronRight' size='small' />
            </Box>
          </Box>
        </React.Fragment>
      ))}
      <Box tag='span' className='SodHeader-breadcrumb-item'>
        <Typography kind='body2'>{title}</Typography>
      </Box>
    </Box>
  );

  const getTitle = () => {
    if (typeof title === 'string') {
      return (
        <Typography
          kind='h1'
          lineLimit={1}
          color={CF_NEU_BLD}
          className='SodHeader-title'>
          {title}
        </Typography>
      );
    }

    return title;
  };

  const getDescription = () => {
    if (!description) {
      return null;
    }

    if (typeof description === 'string') {
      return (
        <Typography
          kind='body2'
          lineLimit={1}
          color={CF_NEU_STL}
          className='SodHeader-description'>
          {description}
        </Typography>
      );
    }

    return description;
  };

  return (
    <header className={headerClasses}>
      <Box className='SodHeader'>
        <Box className='SodHeader-backButton'>
          <ButtonIcon
            size='medium'
            kind='ghost'
            icon={<Icon kind='ChevronLeft' />}
            onClick={handleGoBack ?? defaultGoBack}
          />
        </Box>
        <Box className='SodHeader-center'>
          <ApplicationIcon
            size='large'
            icon={applicationIconKind}
            iconColor={CF_SEC_MED}
          />
          <Box className='SodHeader-textWrapper'>
            {breadcrumbItems?.length ? getBreadcrumb() : null}
            {getTitle()}
            {getDescription()}
          </Box>
        </Box>
        {controls}
      </Box>
      {contentBottom}
    </header>
  );
}

SodHeader.defaultProps = {
  description: null,
  controls: null,
  // eslint-disable-next-line react/jsx-no-useless-fragment
  contentBottom: <></>,
  applicationIconKind: 'SOD',
  className: '',
  handleGoBack: null,
  breadcrumbItems: [],
};

export default SodHeader;
